import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import axios from 'axios';
import { Download, CheckCircle, AlertTriangle, XCircle, Columns } from 'react-feather';
import * as XLSX from 'xlsx';
import config from '../../../../config';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../admin-v3.css';
import v3DataTableStyles from '../v3DataTable';
import v3SelectStyles from '../v3Select';

const portalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: v3SelectStyles,
};

const STATUS_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'active', label: 'Active' },
    { value: 'missing', label: 'Missing' },
    { value: 'inactive', label: 'Inactive' },
];

// Missing-days → status bucket.
const statusOf = (n) => (n === 0 ? 'active' : n <= 30 ? 'missing' : 'inactive');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Configurable columns. Canonical order; default-visible subset; rest opt-in.
const COLUMN_LABELS = {
    tenant_name: 'Tenant', property_name: 'Property', tenant_code: 'Tenant Code',
    tenant_live_date: 'Live Date', total_missing_dates: 'Missing Days', status: 'Status',
    recently_active_date: 'Recently Active', group_name: 'Group', outlet_code: 'Unit Code',
    total_live_days: 'Live Days',
};
const ALL_COLUMN_KEYS = ['tenant_name', 'property_name', 'tenant_code', 'tenant_live_date', 'total_missing_dates', 'status', 'recently_active_date', 'group_name', 'outlet_code', 'total_live_days'];
const DEFAULT_COLUMN_KEYS = ['tenant_name', 'property_name', 'tenant_code', 'tenant_live_date', 'total_missing_dates', 'status', 'recently_active_date'];
const COLUMN_OPTIONS = ALL_COLUMN_KEYS.map((k) => ({ value: k, label: COLUMN_LABELS[k] }));
const LS_COLUMNS = 'hc_visible_columns';

const HealthCheckup = () => {
    const { t } = useTranslation();
    const canView = localStorage.getItem('accessHealthCheckup') === '1';
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const property_access = localStorage.getItem('propertyAccess');
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    const [properties, setProperties] = useState([]);
    const [property_id, setPropertyId] = useState('');
    const [status, setStatus] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleKeys, setVisibleKeys] = useState(() => {
        try { const s = JSON.parse(localStorage.getItem(LS_COLUMNS)); return Array.isArray(s) && s.length ? s : DEFAULT_COLUMN_KEYS; }
        catch { return DEFAULT_COLUMN_KEYS; }
    });

    const setColumns = (opts) => {
        let keys = (opts || []).map((o) => o.value);
        if (!keys.length) keys = DEFAULT_COLUMN_KEYS; // never allow an empty table
        setVisibleKeys(keys);
        localStorage.setItem(LS_COLUMNS, JSON.stringify(keys));
    };

    useEffect(() => {
        if (!canView) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canView]);

    useEffect(() => {
        axios
            .post(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaire_id, property_id: property_access }, configheader)
            .then((res) => setProperties(res.data || []))
            .catch((err) => console.log(err));
        setLoading(true);
        axios
            .post(`${config.hostname}/reports/gethealthcheckup`, { concessionaire_id }, configheader)
            .then((res) => setReportData(res.data || []))
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, []);

    const propertyOptions = useMemo(
        () => [{ value: '', label: t('All properties') }, ...properties.map((p) => ({ value: p._id, label: p.property_name }))],
        [properties, t],
    );

    // Translated copies of the module-scope option lists (labels stay English in data).
    const statusOptions = STATUS_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));
    const columnOptions = COLUMN_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));

    const filtered = useMemo(() => {
        let d = reportData;
        if (property_id) {
            const sp = properties.find((p) => p._id === property_id);
            if (sp) d = d.filter((i) => i.property_name === sp.property_name);
        }
        if (status) d = d.filter((i) => statusOf(i.total_missing_dates) === status);
        return d;
    }, [reportData, properties, property_id, status]);

    const counts = useMemo(() => {
        const c = { active: 0, missing: 0, inactive: 0 };
        reportData.forEach((i) => { c[statusOf(i.total_missing_dates)] += 1; });
        return c;
    }, [reportData]);

    const handleExport = () => {
        const rows = filtered.map((item) => ({
            [t('Tenant Name')]: item.tenant_name,
            [t('Group Name')]: item.group_name,
            [t('Property')]: item.property_name,
            [t('Tenant Code')]: item.tenant_code,
            [t('Unit Code')]: item.outlet_code,
            [t('Total Live Days')]: item.total_live_days,
            [t('Total Missing Days')]: item.total_missing_dates,
            [t('Status')]: statusOf(item.total_missing_dates),
            [t('Recently Active')]: fmtDate(item.recently_active_date),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Health Checkup');
        XLSX.writeFile(wb, `LiveHealthCheckup_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const badge = (n) => {
        const s = statusOf(n);
        const cls = s === 'active' ? 'green' : s === 'missing' ? 'amber' : 'red';
        const label = s === 'active' ? t('Active') : s === 'missing' ? t('Missing') : t('Inactive');
        return <span className={`v3-badge ${cls}`}>{label}</span>;
    };

    const columnDefs = {
        tenant_name: { name: t('Tenant'), selector: (r) => r.tenant_name, sortable: true, wrap: true, minWidth: '180px' },
        group_name: { name: t('Group'), selector: (r) => r.group_name, sortable: true, wrap: true, minWidth: '140px' },
        property_name: { name: t('Property'), selector: (r) => r.property_name, sortable: true, wrap: true, minWidth: '130px' },
        tenant_code: { name: t('Tenant Code'), selector: (r) => r.tenant_code, sortable: true, minWidth: '110px' },
        outlet_code: { name: t('Unit Code'), selector: (r) => r.outlet_code, sortable: true, minWidth: '100px' },
        tenant_live_date: { name: t('Live Date'), selector: (r) => r.tenant_live_date, sortable: true, minWidth: '120px', format: (r) => fmtDate(r.tenant_live_date) },
        total_live_days: { name: t('Live Days'), selector: (r) => r.total_live_days, sortable: true, minWidth: '100px', right: true },
        total_missing_dates: {
            name: t('Missing Days'), selector: (r) => r.total_missing_dates, sortable: true, minWidth: '120px', right: true,
            cell: (r) => <span className="dash-val">{r.total_missing_dates}</span>,
        },
        status: { name: t('Status'), sortable: false, minWidth: '110px', cell: (r) => badge(r.total_missing_dates) },
        recently_active_date: { name: t('Recently Active'), selector: (r) => r.recently_active_date, sortable: true, minWidth: '130px', format: (r) => fmtDate(r.recently_active_date) },
    };
    // Canonical order, filtered to the user's visible set.
    const columns = ALL_COLUMN_KEYS.filter((k) => visibleKeys.includes(k)).map((k) => columnDefs[k]);

    if (!canView) return null;

    const kpi = (icon, tone, value, label) => (
        <div className="v3-card v3-sadmin-kpi">
            <div className="v3-sadmin-kpi-top">
                <span className={`v3-sadmin-kpi-icon ${tone}`} aria-hidden>{icon}</span>
            </div>
            <div className="v3-sadmin-kpi-value">{value}</div>
            <div className="dash-muted" style={{ fontSize: 12 }}>{label}</div>
        </div>
    );

    return (
        <Fragment>
            <div className="v3-sadmin v3-admin-page">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Live Health Checkup')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">{t('Tenant data-feed health — live days, missing days and recent activity.')}</p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <Select
                            {...portalProps}
                            options={propertyOptions}
                            value={propertyOptions.find((o) => o.value === property_id) || propertyOptions[0]}
                            onChange={(opt) => setPropertyId(opt ? opt.value : '')}
                            isSearchable
                            placeholder={t('Search property…')}
                            aria-label={t('Property filter')}
                        />
                        <Select
                            {...portalProps}
                            options={statusOptions}
                            value={statusOptions.find((o) => o.value === status)}
                            onChange={(opt) => setStatus(opt ? opt.value : '')}
                            isSearchable={false}
                            aria-label={t('Status filter')}
                        />
                        <button type="button" className="v3-btn v3-btn-primary" onClick={handleExport} disabled={!filtered.length}>
                            <Download size={15} /> {t('Export')}
                        </button>
                    </div>
                </div>

                <div className="v3-sadmin-kpis">
                    {kpi(<CheckCircle size={18} />, 'green', counts.active, t('Active (0 missing days)'))}
                    {kpi(<AlertTriangle size={18} />, 'amber', counts.missing, t('Missing (1–30 days)'))}
                    {kpi(<XCircle size={18} />, 'red', counts.inactive, t('Inactive (>30 days)'))}
                </div>

                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-title">{t('Tenants')}</h2>
                            <span className="v3-sadmin-list-count">{filtered.length}</span>
                        </div>
                        <div className="v3-sadmin-list-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Columns size={15} color="var(--v3-text-muted)" aria-hidden />
                            <Select
                                {...portalProps}
                                isMulti
                                options={columnOptions}
                                value={columnOptions.filter((o) => visibleKeys.includes(o.value))}
                                onChange={setColumns}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isClearable={false}
                                placeholder={t('Columns…')}
                                aria-label={t('Configure columns')}
                                styles={{ ...v3SelectStyles, container: (b) => ({ ...b, minWidth: 240, maxWidth: 380 }) }}
                            />
                        </div>
                    </div>
                    <div style={{ padding: '4px 8px 8px' }}>
                        <DataTable
                            columns={columns}
                            data={filtered}
                            progressPending={loading}
                            pagination
                            paginationPerPage={50}
                            highlightOnHover
                            responsive
                            customStyles={v3DataTableStyles}
                            noDataComponent={t('No records found.')}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default HealthCheckup;
